import formatCurrency from "format-currency";
import { get, isEmpty } from "lodash-es";
import Vue from "vue";

import { getBaseURLDashboard } from "@/middleware/__hooks/base-url-dashboard.hook";
import { COPYRIGHT_TEXT } from "@/shared/constants/copyright";
import { FALCON_EDGE_FULL_BLACK } from "@/src/image";
import errorMap from "@/static/data/errorMap";
import successMap from "@/static/data/successMap";
import version from "@/static/version.json";

const dummyLogo =
  process.env.PARTNER_CODE === "GENERAL"
    ? FALCON_EDGE_FULL_BLACK
    : `https://falconnedge.com/images/logo/${process.env.PARTNER_CODE?.toLocaleLowerCase()}.png`;

export default ({ app }) => {
  Vue.mixin({
    computed: {
      // check if it's qoala
      isQoala() {
        return app.$role.isQoala();
      },
      // check if it's qoala CS
      isQoalaCS() {
        return app.$role.isQoalaCS();
      },
      // check if it's qoala Campaign
      isQoalaCampaign() {
        return app.$role.isQoalaCampaign();
      },
      // check if it's qoala admin
      isQoalaAdmin() {
        return app.$role.isQoalaAdmin();
      },
      // check if it's qoala super admin
      isQoalaSuperAdmin() {
        return app.$role.isQoalaSuperAdmin();
      },
      // check if it's qoala areps
      isQoalaAreps() {
        return app.$role.isQoalaAreps();
      },
      isInsurer() {
        return app.$role.isInsurer();
      },
      isPartner() {
        return app.$role.isPartner();
      },
      isServiceCenter() {
        return app.$role.isServiceCenter();
      },
      isCompany() {
        return app.$role.isCompany();
      },
      // get module (health/electronic/gadget etc from route)
      getModule() {
        return app.$moduleType.getModule();
      },
      getListingType() {
        return app.$moduleType.getListingType();
      },
      // modal helper
      modalData() {
        return this.$store.getters["app/getModal"];
      },
      userOrganisationLogo() {
        let logoImage = dummyLogo;
        const logoDB = this.$store.getters["appPersisted/getLogo"];

        if (logoDB !== "") {
          logoImage = logoDB;
        }

        if (logoDB === null) {
          logoImage = dummyLogo;
        }

        return logoImage;
      },
      // === partner/insurance/role checking
      isJDID() {
        return get(this.$store.getters.getUserData, "partner.id", null) === 33;
      },
      isLippoInsurance() {
        return (
          get(this.$store.getters.getUserData, "insurance.id", null) === 11
        );
      },
      isInternalDashboard() {
        return process.env.NODE_ENV === "internal";
      },
      // exporting from campaign
      errorMap() {
        return errorMap;
      },
      successMap() {
        return successMap;
      },
      isCampaignManagement() {
        const organisation = get(
          this.$store.getters["auth/getUserData"],
          "organization",
          {},
        );

        return (
          get(organisation, "type", "").toLowerCase() === "qoala" &&
          get(organisation, "code", "").toLowerCase() === "qoala-campaign"
        );
      },
      hasQAppPermission() {
        return (
          this.hasPermission("common.promotion.campaign.qoalaApp.create") ||
          this.hasPermission("common.promotion.campaign.qoalaApp.edit")
        );
      },
      hasQPlusPermission() {
        return (
          this.hasPermission("common.promotion.campaign.qoalaPlus.create") ||
          this.hasPermission("common.promotion.campaign.qoalaPlus.edit")
        );
      },
      hasFairdeePermission() {
        return (
          this.hasPermission("common.promotion.campaign.fairdee.create") ||
          this.hasPermission("common.promotion.campaign.fairdee.edit")
        );
      },
      qappPermission() {
        const permission = {};
        ["view", "create", "edit", "remove", "start", "stop"].forEach(
          (type) => {
            permission[type] =
              this.isQoalaSuperAdmin ||
              this.hasPermission(
                `common.promotion.campaign.qoalaApp.${type}`,
              ) ||
              this.hasPermission(`common.promotion.campaign.${type}`);
          },
        );
        return permission;
      },
      qplusPermission() {
        const permission = {};
        ["view", "create", "edit", "remove", "start", "stop"].forEach(
          (type) => {
            permission[type] =
              this.isQoalaSuperAdmin ||
              this.hasPermission(
                `common.promotion.campaign.qoalaPlus.${type}`,
              ) ||
              this.hasPermission(`common.promotion.campaign.${type}`);
          },
        );
        return permission;
      },
      fairDeePermission() {
        const permission = {};
        ["view", "create", "edit", "remove", "start", "stop"].forEach(
          (type) => {
            permission[type] =
              this.isQoalaSuperAdmin ||
              this.hasPermission(`common.promotion.campaign.fairdee.${type}`) ||
              this.hasPermission(`common.promotion.campaign.${type}`);
          },
        );
        return permission;
      },
      pshipPermission() {
        const permission = {};
        ["view", "create", "edit", "remove", "start", "stop"].forEach(
          (type) => {
            permission[type] =
              this.isQoalaSuperAdmin ||
              this.hasPermission(
                `common.promotion.campaign.partnership.${type}`,
              ) ||
              this.hasPermission(`common.promotion.campaign.${type}`);
          },
        );
        return permission;
      },
      adbPermission() {
        const permission = {};
        ["view", "create", "edit", "remove", "start", "stop"].forEach(
          (type) => {
            permission[type] =
              this.isQoalaSuperAdmin ||
              this.hasPermission(`common.promotion.campaign.adb.${type}`) ||
              this.hasPermission(`common.promotion.campaign.${type}`);
          },
        );
        return permission;
      },
      partnerListForFilter() {
        if (this.isQoalaAreps) {
          return (
            get(
              this.$store.getters["auth/getUserData"],
              "represents.partners",
              [],
            ) || []
          );
        }
        if (this.isQoalaAdmin) {
          return (
            this.$store.getters[`products/${this.getModule}/getPartners`] || []
          ).filter((partner) => partner.type === "partner");
        }
        return [];
      },
      insuranceListForFilter() {
        if (this.isQoalaAdmin) {
          return (
            this.$store.getters[`products/${this.getModule}/getPartners`] || []
          ).filter((partner) => partner.type === "insurance");
        }
        return [];
      },
      mobileFilterMenus() {
        let filterMenus = [];
        if (this.isQoalaAreps || this.isQoalaAdmin) {
          filterMenus = ["dateRange", "partners", "policyType"];
          if (this.isQoalaAdmin) {
            filterMenus.splice(2, 0, "insurances");
          }
        } else if (this.isPartner) {
          filterMenus = ["dateRange", "policyType"];
        }
        return filterMenus;
      },
      isInternalPortal() {
        const internalDomain = process.env.INTERNAL_PORTAL_DOMAIN.split(",");
        return internalDomain.includes(window.location.host);
      },
      isExternalPortal() {
        const externalDomain = process.env.EXTERNAL_PORTAL_DOMAIN.split(",");
        return externalDomain.includes(window.location.host);
      },
      isGeneralPortal() {
        return process.env.PARTNER_CODE === "GENERAL";
      },
      partnerBrandingName() {
        const partnerCode = process.env.PARTNER_CODE;

        return partnerCode && partnerCode !== "GENERAL"
          ? partnerCode.charAt(0).toUpperCase() +
              partnerCode.slice(1).toLowerCase()
          : "FalconEdge";
      },
      copyrightText() {
        const partnerCode = process.env.PARTNER_CODE;

        if (!partnerCode || partnerCode === "GENERAL") {
          return "FalconEdge";
        }

        const copyrightConfig = COPYRIGHT_TEXT[partnerCode];
        const baseText =
          copyrightConfig?.text ||
          partnerCode.charAt(0).toUpperCase() +
            partnerCode.slice(1).toLowerCase();

        return copyrightConfig?.isNeedVersion
          ? `${baseText} v${version.version}`
          : baseText;
      },
    },
    watch: {
      isLoadingFirstTime(newValue) {
        if (`${get(this, "$options.name")}`.toLowerCase().includes("page")) {
          this.$store.dispatch("app/setPageLoadingFirstTime", newValue);
        }

        if (!newValue) {
          // reset route on changed vuex store
          this.$store.dispatch("app/setRouteOnChanged", false);
        }
      },
    },
    methods: {
      logOut() {
        // destroy cookie
        this.$store.dispatch("auth/authDestroy");
        //destroy LocalStorage
        localStorage.removeItem("lastRefresh");
        // remove pageTitle
        this.$store.dispatch("app/setPageInfo", {
          pageTitle: null,
        });
      },
      dateFilterShort(value, withoutStrip) {
        if (isEmpty(value)) return "-";
        return app
          .$dateFunc({ input: value })
          .format(withoutStrip ? "DD MMM YYYY" : "DD-MMM-YYYY");
      },
      dateTimeFilter(value) {
        if (isEmpty(value)) return "-";
        return app.$dateFunc({ input: value }).format("DD MMM YYYY HH:mm");
      },
      getPhoneNumber(value) {
        if (!value) return "";
        if (value[0] === "0") return value.substr(1);
        if (value[0] === "+") return value.substr(3);
        return value;
      },
      onlyNumber(data) {
        return data.replace(/\D/g, "");
      },
      rupiahCurrency(value) {
        const dataVal = value || 0;
        const returnCurrency = formatCurrency(dataVal, {
          symbol: "Rp ",
          format: "%s%v",
          locale: "id",
        });
        return returnCurrency.replace(",00", "");
      },
      decimalCurrency(data) {
        if (data.length < 4) return data;
        return data.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      },
      // checking permission
      hasPermission(permission) {
        return app.$permission.hasPermission(permission);
      },
      // checking permission
      hasPermissions(reqPermissions, operator = "or") {
        return app.$permission.hasPermissions(reqPermissions, operator);
      },
      redirectMainDashboard() {
        // dynamic choosing from first selected menu
        // choose menu based on dashboard
        const selectedMenu = getBaseURLDashboard(app.context);
        this.$router.push(selectedMenu);
      },
      // exporting from campaign
      toggleSuccess(success, data = {}, color = "invert-success") {
        let message = success.message || success;
        if (success.code && this.successMap[success.code]) {
          message = this.$helpers.replaceStrVar(
            this.successMap[success.code],
            data,
          );
        }
        this.$store.dispatch("app/toggleSnackBar", {
          message,
          color,
        });
      },
      toggleError(error, data = {}, color = "invert-error") {
        let message = error.message || error;
        if (error.code && this.errorMap[error.code]) {
          message = this.$helpers.replaceStrVar(
            this.errorMap[error.code],
            data,
          );
        }
        this.$store.dispatch("app/toggleSnackBar", {
          message,
          color,
        });
      },
      trimZero(value) {
        return value.toString().replace(/^0+/g, "") || 0;
      },
      toCurrency(val, countryId = "ID") {
        const format = {
          symbol: `${this.$helpers.getCurrencyByCountry(countryId)} `,
          format: "%s%v",
          locale: countryId.toLowerCase(),
        };
        return formatCurrency(val || 0, format).replace(",00", "");
      },
      getFormattedNumber(val, type, data = {}) {
        switch (type) {
          case "percentage":
            return `${val} %`;
          case "currency":
            return this.$formatting.convertCurrency(
              val,
              this.$helpers.getCurrencyByCountry(data.countryId),
            );
          default:
            return val;
        }
      },
    },
  });

  /* eslint-disable */
  // converter to number pattern currency
  Number.prototype.formatNumber = function (c, d, t) {
    var n = this,
      c = isNaN((c = Math.abs(c))) ? 2 : c,
      d = d == undefined ? "." : d,
      t = t == undefined ? "," : t,
      s = n < 0 ? "-" : "",
      i = String(parseInt((n = Math.abs(Number(n) || 0).toFixed(c)))),
      j = (j = i.length) > 3 ? j % 3 : 0;
    return (
      s +
      (j ? i.substr(0, j) + t : "") +
      i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${t}`) +
      (c
        ? d +
          Math.abs(n - i)
            .toFixed(c)
            .slice(2)
        : "")
    );
  };

  // for generating password
  // https://stackoverflow.com/a/12635919/9433691
  String.prototype.pick = function (min, max) {
    var n,
      chars = "";

    if (typeof max === "undefined") {
      n = min;
    } else {
      n = min + Math.floor(Math.random() * (max - min));
    }

    for (var i = 0; i < n; i++) {
      chars += this.charAt(Math.floor(Math.random() * this.length));
    }

    return chars;
  };

  // for generating password
  // https://stackoverflow.com/a/12635919/9433691
  // Credit to @Christoph: http://stackoverflow.com/a/962890/464744
  String.prototype.shuffle = function () {
    var array = this.split("");
    var tmp,
      current,
      top = array.length;

    if (top)
      while (--top) {
        current = Math.floor(Math.random() * (top + 1));
        tmp = array[current];
        array[current] = array[top];
        array[top] = tmp;
      }

    return array.join("");
  };
};
